/** biome-ignore-all lint/style/noExportedImports: Adding functionality on top of Cadence component */

/**
 * Accordion component with Next.js FAQ schema support.
 *
 * Wraps the Cadence Accordion with JSON-LD structured data generation
 * for SEO on FAQ pages.
 */
import { Accordion as CadenceAccordion, AccordionContent, AccordionItem, type AccordionProps as CadenceAccordionProps, AccordionTrigger } from '@music-vine/cadence/ui';
import Head from 'next/head';
import { jsonLdScriptProps } from 'react-schemaorg';
import { stripMarkdown } from '@/lib/utils';
import type { FAQPage } from 'schema-dts';
type AccordionProps = CadenceAccordionProps & {
  /**
   * An optional array of objects containing headers and texts for FAQ schema generation.
   * This will be used to create structured data for search engines.
   *
   * @property {Object[]} schemaContent
   * @property {string} schemaContent[].header - The question or title of the FAQ item
   * @property {string} schemaContent[].text - The answer or content of the FAQ item
   *
   * Both header and text will be parsed to remove any Markdown formatting,
   * converting them to plain text for use in the schema.
   */
  schemaContent?: {
    header: string;
    text: string;
  }[];
};
function Accordion({
  schemaContent,
  ...props
}: AccordionProps) {
  const faqPagesSchema: FAQPage['mainEntity'] = schemaContent ? schemaContent.map((item: {
    header: string;
    text: string;
  }) => ({
    '@type': 'Question',
    name: stripMarkdown(item.header),
    acceptedAnswer: {
      '@type': 'Answer',
      text: stripMarkdown(item.text)
    }
  })) : undefined;
  return <>
      {!!schemaContent && <Head>
          <script {...jsonLdScriptProps<FAQPage>({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqPagesSchema
      })} />
        </Head>}
      <CadenceAccordion {...props} data-sentry-element="CadenceAccordion" data-sentry-source-file="accordion.tsx" />
    </>;
}
Accordion.displayName = 'Accordion';
export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };